import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatDisplayInteger } from 'i18n/arabicDigits';

const { Text } = Typography;

export interface LineChartData {
  date: string;
  count: number;
}

export interface LineChartProps {
  data: LineChartData[];
  height?: number;
  color?: string;
  width?: number | string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 200,
  color = 'var(--color-primary, #41aaaf)',
  width = 400,
}) => {
  const { t, i18n } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Text style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>{t('global.no-data-available')}</Text>
      </div>
    );
  }

  const padding = { top: 20, right: 20, bottom: 40, left: 40 };
  // Calculate chart width based on width prop - use larger width for 100%
  const chartWidth = typeof width === 'string' && width === '100%' 
    ? 800  // Use a larger fixed width when 100% is specified to fill 2 columns
    : typeof width === 'number' 
    ? width 
    : 400;
  const chartHeight = height;
  const containerStyle = typeof width === 'string' && width === '100%'
    ? { width: '100%', maxWidth: '100%' }
    : {};
  
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate scales
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const minCount = Math.min(...data.map((d) => d.count), 0);
  // Ensure we have a visible range even for single small values
  const countRange = maxCount - minCount || 1;
  // Add padding to the range to ensure visibility for small values
  const paddedMin = minCount === maxCount ? Math.max(0, minCount - 1) : minCount;
  const paddedMax = minCount === maxCount ? minCount + 1 : maxCount;
  const paddedRange = paddedMax - paddedMin || 1;

  const xScale = (index: number) => {
    if (data.length === 1) {
      // Center single point
      return padding.left + innerWidth / 2;
    }
    return padding.left + (index / (data.length - 1)) * innerWidth;
  };
  const yScale = (value: number) =>
    padding.top +
    innerHeight -
    ((value - paddedMin) / paddedRange) * innerHeight;

  // Generate path for line
  const pathData = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.count)}`)
    .join(' ');

  // Generate area path (for fill) - use paddedMin as the baseline
  const areaPath =
    pathData +
    ` L ${xScale(data.length - 1)} ${yScale(paddedMin)} L ${xScale(0)} ${yScale(paddedMin)} Z`;

  // For single data point, draw vertical line from x-axis to the point
  const isSinglePoint = data.length === 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ...containerStyle }}>
      <svg 
        width={chartWidth} 
        height={chartHeight} 
        style={{ overflow: 'visible', maxWidth: '100%' }}
      >
        {/* Grid lines */}
        {[0, 1, 2, 3, 4].map((i) => {
          const y = padding.top + (innerHeight / 4) * i;
          return (
            <line
              key={`grid-${i}`}
              x1={padding.left}
              y1={y}
              x2={padding.left + innerWidth}
              y2={y}
              stroke="#F3F7F8"
              strokeWidth="1"
            />
          );
        })}

        {/* Area fill */}
        <path
          d={areaPath}
          fill={color}
          fillOpacity="0.1"
        />

        {/* Line */}
        <path
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Vertical line for single data point */}
        {isSinglePoint && (
          <line
            x1={xScale(0)}
            y1={padding.top + innerHeight}
            x2={xScale(0)}
            y2={yScale(data[0].count)}
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        )}

        {/* Points */}
        {data.map((d, i) => (
          <g key={`point-${i}`}>
            <circle
              cx={xScale(i)}
              cy={yScale(d.count)}
              r="4"
              fill={color}
              stroke="#ffffff"
              strokeWidth="2"
            />
            {/* Tooltip on hover */}
            <circle
              cx={xScale(i)}
              cy={yScale(d.count)}
              r="8"
              fill="transparent"
              style={{ cursor: 'pointer' }}
            >
              <title>{`${d.date}: ${formatDisplayInteger(d.count, i18n.language)}`}</title>
            </circle>
          </g>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => {
          // Show every nth label to avoid crowding
          const showLabel = data.length <= 7 || i % Math.ceil(data.length / 7) === 0 || i === data.length - 1;
          if (!showLabel) return null;
          
          return (
            <text
              key={`x-label-${i}`}
              x={xScale(i)}
              y={chartHeight - padding.bottom + 20}
              textAnchor="middle"
              fontSize="10"
              fill='var(--color-text-muted, #5a6a6a)'
            >
              {d.date.length > 14 ? `${d.date.substring(0, 14)}…` : d.date}
            </text>
          );
        })}

        {/* Y-axis labels */}
        {[0, 1, 2, 3, 4].map((i) => {
          const value = Math.round(paddedMin + (paddedRange / 4) * (4 - i));
          return (
            <text
              key={`y-label-${i}`}
              x={padding.left - 10}
              y={padding.top + (innerHeight / 4) * i + 4}
              textAnchor="end"
              fontSize="10"
              fill='var(--color-text-muted, #5a6a6a)'
            >
              {formatDisplayInteger(value, i18n.language)}
            </text>
          );
        })}

        {/* Axes */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + innerHeight}
          stroke="var(--color-border, #dbeae3)"
          strokeWidth="1"
        />
        <line
          x1={padding.left}
          y1={padding.top + innerHeight}
          x2={padding.left + innerWidth}
          y2={padding.top + innerHeight}
          stroke="var(--color-border, #dbeae3)"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
};

